/**
 * Design Language's light/dark choice, and keeping the page in it.
 *
 * Until Design Language got its own shell it shared Elevation's switch (the
 * header's toggle, stored as "elevation-theme", applied by App.tsx's
 * ElevationThemeEnforcer). Team pages lost that header, so nobody could switch
 * theme there. Design Language now keeps its own choice, and on first visit
 * starts from whatever the shared one was, so nobody's view changes on the day
 * it separated.
 *
 * Light pins the whole document to light (ThemeContext.setForcedLight) without
 * touching the person's app-wide preference; dark releases the pin, and the
 * shell's wrapper carries the .dark class so the daylight tokens remap onto
 * the dark ramp — the same two moves ElevationThemeEnforcer and
 * AppElevationBody make on Elevation's own pages.
 */
import { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

export type DesignLanguageTheme = 'light' | 'dark';

/** Where Design Language keeps its choice in this browser. */
export const DESIGN_LANGUAGE_THEME_KEY = 'design-language-theme';

/** The choice it shared with Elevation before; read only until it has its own. */
const SHARED_THEME_KEY_BEFORE = 'elevation-theme';

function readStored(key: string): DesignLanguageTheme | null {
  try {
    const value = localStorage.getItem(key);
    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    return null; // storage unavailable (private mode)
  }
}

export function initialDesignLanguageTheme(): DesignLanguageTheme {
  return readStored(DESIGN_LANGUAGE_THEME_KEY) ?? readStored(SHARED_THEME_KEY_BEFORE) ?? 'light';
}

export function useDesignLanguageTheme(): [DesignLanguageTheme, (theme: DesignLanguageTheme) => void] {
  const [theme, setThemeState] = useState<DesignLanguageTheme>(initialDesignLanguageTheme);
  const { setForcedLight } = useTheme();

  useEffect(() => {
    setForcedLight(theme === 'light');
    return () => setForcedLight(false);
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps -- setForcedLight is a new function on every provider render

  const setTheme = (next: DesignLanguageTheme) => {
    try {
      localStorage.setItem(DESIGN_LANGUAGE_THEME_KEY, next);
    } catch {
      // storage unavailable: the choice holds for this visit only
    }
    setThemeState(next);
  };

  return [theme, setTheme];
}
